import { useContext, useEffect, useReducer } from 'react';

import { UserDetailContext, UserDetailContextProps } from '../../context/userDetail/userDetailContext';
import { GRID_ACTIONS, gridReducer, IGridReducerState } from '../../reducer/gridReducer/gridReducer';
import { getSelectedCustomerId } from '../../helpers/customer';
import { useAuthState } from '../../providers/authProvider';
import { getInitialType } from '../../utils/getInitialTypes';
import { SearchDetailContext, SearchDetailContextProps } from '../../context/searchDetail/searchDetailContext';
import { RoutingToTypes } from '../search/SearchActions/SearchActions';

import DocumentRepositoryPage from './DocumentRepositoryPage';
import {
  getGenericDocumentRepository,
  getSpecificDocumentRepository,
  downloadGenericDocument,
  downloadSpecificDocument,
} from '../../common/api/documentRepositoryApi';
import { isTpiUser } from '../../helpers/tpiUser';

const DocumentRepository = () => {
  const { userDetail } = useContext<UserDetailContextProps>(UserDetailContext);
  const searchContext = useContext<SearchDetailContextProps>(SearchDetailContext);
  const authContext = useAuthState();

  const initialSpecificDocumentsState: IGridReducerState = {
    page: 1,
    currentType: '',
    data: {},
    initialSortField: '',
    initialSortOrder: '',
    currentSortField: '',
    currentSortOrder: '',
    isLoading: false,
    hasMoreData: true,
    selectedCustomerId: getSelectedCustomerId(userDetail, true),
  };
  const initialGenericDocumentsState: IGridReducerState = {
    page: 1,
    currentType: '',
    data: {},
    initialSortField: '',
    initialSortOrder: '',
    currentSortField: '',
    currentSortOrder: '',
    isLoading: false,
    hasMoreData: true,
    selectedCustomerId: getSelectedCustomerId(userDetail, true),
  };

  const [stateSpecific, dispatchSpecific] = useReducer(gridReducer, initialSpecificDocumentsState);
  const [stateGeneric, dispatchGeneric] = useReducer(gridReducer, initialGenericDocumentsState);

  const tpiUser = isTpiUser();
  const userType = tpiUser ? 'TPIUser' : 'Customer';

  const loadSpecificDocumentsData = async () => {
    dispatchSpecific({ type: GRID_ACTIONS.CHANGE_LOADING, payload: true });

    const customerAccounts = tpiUser ? [] : userDetail.customerAccounts.map(x => x.essAccountID);

    const data = getSpecificDocumentRepository(
      process.env.REACT_APP_API_URL,
      authContext,
      stateSpecific.page,
      userType,
      customerAccounts
    );

    data.then((result: Common.IResult<Api.IDocumentRepository>) => {
      if (result && result.success) {
        dispatchSpecific({ type: GRID_ACTIONS.ADD_DATA, payload: { data: result.data, page: stateSpecific.page } });
      } else {
        dispatchSpecific({ type: GRID_ACTIONS.CLEAR_DATA });
      }
      dispatchSpecific({ type: GRID_ACTIONS.CHANGE_LOADING, payload: false });
    });
  };

  const loadGenericDocumentsData = async () => {
    dispatchGeneric({ type: GRID_ACTIONS.CHANGE_LOADING, payload: true });

    const data = getGenericDocumentRepository(process.env.REACT_APP_API_URL, authContext, stateSpecific.page, userType);

    data.then((result: Common.IResult<Api.IDocumentRepository>) => {
      if (result && result.success) {
        dispatchGeneric({ type: GRID_ACTIONS.ADD_DATA, payload: { data: result.data, page: stateSpecific.page } });
      } else {
        dispatchGeneric({ type: GRID_ACTIONS.CLEAR_DATA });
      }
      dispatchGeneric({ type: GRID_ACTIONS.CHANGE_LOADING, payload: false });
    });
  };

  // When navigating directly to url
  useEffect(() => {
    if (userDetail.essCustomerId) {
      // Specific
      initialSpecificDocumentsState.currentType = getInitialType(
        userDetail.hasElectricity,
        userDetail.hasGas,
        userDetail.hasRenewable
      );
      const selectedCustomerAccount = userDetail?.selectedCustomerAccount;
      if (selectedCustomerAccount.length > 0)
        initialSpecificDocumentsState.selectedCustomerId = selectedCustomerAccount;
      else initialSpecificDocumentsState.selectedCustomerId = getSelectedCustomerId(userDetail, true);
      dispatchSpecific({ type: GRID_ACTIONS.REINIT, payload: initialSpecificDocumentsState });

      // Generic
      initialGenericDocumentsState.currentType = getInitialType(
        userDetail.hasElectricity,
        userDetail.hasGas,
        userDetail.hasRenewable
      );
      if (selectedCustomerAccount.length > 0) initialGenericDocumentsState.selectedCustomerId = selectedCustomerAccount;
      else initialGenericDocumentsState.selectedCustomerId = getSelectedCustomerId(userDetail, true);
      dispatchGeneric({ type: GRID_ACTIONS.REINIT, payload: initialGenericDocumentsState });
    }
    // TODO:  Might want to remove this and revisit this problem later
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetail.hasElectricity, userDetail.hasGas, userDetail.hasRenewable]);

  useEffect(() => {
    if (userDetail.customerAccounts.length === 0) return;

    loadSpecificDocumentsData();
    loadGenericDocumentsData();
  }, [stateSpecific.page, userDetail.customerAccounts]);

  const handleSpecificDocumentsPagingChange = () => {
    dispatchSpecific({ type: GRID_ACTIONS.INCREMENT_PAGE });
  };

  const handleGenericDocumentsPagingChange = () => {
    dispatchGeneric({ type: GRID_ACTIONS.INCREMENT_PAGE });
  };

  const handleDownloadSpecificDocument = async (id: number) => {
    await downloadSpecificDocument(process.env.REACT_APP_API_URL, authContext, id);
  };

  const handleDownloadGenericDocument = async (id: number) => {
    await downloadGenericDocument(process.env.REACT_APP_API_URL, authContext, id);
  };

  return (
    <DocumentRepositoryPage
      email={'myenergysupply@drax.com'}
      isSpecificLoading={stateSpecific.isLoading}
      isGenericLoading={stateGeneric.isLoading}
      //
      gridDataSpecificDocuments={stateSpecific.gridData}
      hasMoreSpecificDocumentData={stateSpecific.hasMoreData}
      handleSpecificDocumentsPagingChange={handleSpecificDocumentsPagingChange}
      handleDownloadSpecificDocument={handleDownloadSpecificDocument}
      //
      gridDataGenericDocuments={stateGeneric.gridData}
      hasMoreGenericDocumentData={stateGeneric.hasMoreData}
      handleGenericDocumentsPagingChange={handleGenericDocumentsPagingChange}
      handleDownloadGenericDocument={handleDownloadGenericDocument}
    ></DocumentRepositoryPage>
  );
};

export default DocumentRepository;
